import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Icon, Spinner } from '@chakra-ui/react';
import {
  ChevronDown,
  ChevronUp,
  Lightning01,
  PauseSquare,
  StopSquare,
} from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { CompletedCheckBadge } from '@/components/shared/CompletedCheckBadge';
import { useShowToast } from '@/hooks/useShowToast';
import { getActivityKindIcon } from '@/components/automation/MoveWithAutomationModal/activityKindIcon';
import type { ActivityKind } from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/types';
import {
  getActivityActionLabel,
  stripStageSuffix,
} from './activityActionLabel';
import { getUnderwayHeading } from './underwayHeading';
import { formatAutomationError } from './automationErrorLabels';
import { getAutomationApiErrorCopy } from '@/components/automation/automationApiErrorCopy';
import { useUnderwayStore } from './useUnderwayStore';
import { useUnderwayCancel } from './useUnderwayCancel';
import { useUnderwayDispatcher } from './useUnderwayDispatcher';
import type { UnderwayActivity } from './types';
import { UNDERWAY_POPUP_STRINGS } from './underwayPopup.strings';
import { StopAutomationConfirmModal } from './StopAutomationConfirmModal';
import { SelectedCandidatesModal } from './SelectedCandidatesModal';
import { UnderwayItemDescription } from './UnderwayItemDescription';

const STR = UNDERWAY_POPUP_STRINGS.item;
const TOAST = UNDERWAY_POPUP_STRINGS.toasts;

type UnderwayItemProps = {
  activity: UnderwayActivity;
};

const titleByStatus = (
  status: UnderwayActivity['status'],
  label: string
): string => {
  switch (status) {
    case 'paused':
      return STR.pausedPrefix(label);
    case 'running':
      return STR.runningPrefix(label);
    case 'completed':
      return STR.completedPrefix(label);
    case 'failed':
      return STR.failedPrefix(label);
    case 'cancelled':
      return STR.cancelledPrefix(label);
    case 'pending':
    default:
      return STR.pendingPrefix(label);
  }
};

// Title copy for a DELAYED first step: after the undo it's triggered but the BE
// runs it after a delay, so it reads "scheduled" / "Scheduling…" rather than
// "starting soon" / "sent". Paused / stopped / failed keep the shared wording.
const scheduledTitleByStatus = (
  status: UnderwayActivity['status'],
  label: string
): string => {
  switch (status) {
    case 'running':
      return STR.scheduledRunningPrefix(label);
    case 'completed':
      return STR.scheduledCompletedPrefix(label);
    case 'paused':
      return STR.pausedPrefix(label);
    case 'failed':
      return STR.failedPrefix(label);
    case 'cancelled':
      return STR.cancelledPrefix(label);
    case 'pending':
    default:
      return STR.scheduledPendingPrefix(label);
  }
};

export const UnderwayItem = ({ activity }: UnderwayItemProps) => {
  const pause = useUnderwayStore((s) => s.pause);
  const resume = useUnderwayStore((s) => s.resume);
  // Widget-level hover (set by UnderwayHoverPauseBoundary) pauses EVERY row.
  const isWidgetHovered = useUnderwayStore((s) => s.isWidgetHovered);
  const setWidgetHovered = useUnderwayStore((s) => s.setWidgetHovered);
  const setStatus = useUnderwayStore((s) => s.setStatus);
  const dismiss = useUnderwayStore((s) => s.dismiss);
  const cancelExecutions = useUnderwayCancel();
  const { fire } = useUnderwayDispatcher();
  const toast = useShowToast();

  const [isHovering, setIsHovering] = useState(false);
  const [isStopModalOpen, setIsStopModalOpen] = useState(false);
  const [isCandidatesModalOpen, setIsCandidatesModalOpen] = useState(false);
  // Accordion: the title row is always visible; the description body
  // collapses. Default expanded so the details show on first paint.
  const [isExpanded, setIsExpanded] = useState(true);

  // Re-render trigger for the gradient bar. Stored as a Date.now()
  // value so render-time math can derive it without calling Date.now
  // during render (React purity rule).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (activity.status !== 'pending') return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 100);
    return () => clearInterval(id);
  }, [activity.status]);

  const progressPct = useMemo(() => {
    if (activity.status === 'paused') {
      return Math.max(
        0,
        Math.min(
          100,
          ((activity.delayMs - activity.remainingMs) / activity.delayMs) * 100
        )
      );
    }
    if (activity.status === 'pending') {
      const elapsed = nowMs - activity.createdAt;
      return Math.max(0, Math.min(100, (elapsed / activity.delayMs) * 100));
    }
    return 100;
  }, [
    nowMs,
    activity.status,
    activity.createdAt,
    activity.delayMs,
    activity.remainingMs,
  ]);

  // Fresh `nowMs` for the first resumed render — a stale one paints the bar
  // one frame backwards before the next tick corrects it.
  const markResumeClock = useCallback(() => setNowMs(Date.now()), []);

  // Pause while the widget is hovered or one of this row's modals is open;
  // resume when all clear, refreshing the clock in the same commit.
  useEffect(() => {
    const shouldPause =
      isWidgetHovered || isStopModalOpen || isCandidatesModalOpen;
    if (shouldPause && activity.status === 'pending') pause(activity.id);
    else if (!shouldPause && activity.status === 'paused') {
      // Deliberate: same-commit refresh keeps the first resumed paint fresh.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      markResumeClock();
      resume(activity.id);
    }
  }, [
    isWidgetHovered,
    isStopModalOpen,
    isCandidatesModalOpen,
    activity.status,
    activity.id,
    pause,
    resume,
    markResumeClock,
  ]);

  // The modal overlay covers the pointer, so the boundary's mouseleave never
  // fires — clear the hover flag here or it sticks and rows stay paused after
  // close. The open modal still pauses THIS row via its own flag.
  const handleStopClick = useCallback(() => {
    setWidgetHovered(false);
    setIsStopModalOpen(true);
  }, [setWidgetHovered]);
  const handleOpenCandidates = useCallback(() => {
    setWidgetHovered(false);
    setIsCandidatesModalOpen(true);
  }, [setWidgetHovered]);
  const handleStopCancel = useCallback(() => {
    setIsStopModalOpen(false);
  }, []);
  const handleStopConfirm = useCallback(async () => {
    setIsStopModalOpen(false);
    try {
      const result = await cancelExecutions(activity);
      // Stop is a terminal user action — drop the row from the popup
      // immediately. The toast (when partial) gives the user the only
      // signal they need; lingering "stopped" rows added noise without
      // adding undo capability.
      dismiss(activity.id);
      if (result?.skipped_ids?.length) {
        toast({ title: TOAST.partiallyStopped, status: 'warning' });
      }
    } catch (err) {
      // Failure path keeps the row so the user can see what went wrong
      // and retry. Only the happy path auto-dismisses.
      setStatus(
        activity.id,
        'failed',
        err instanceof Error ? err.message : 'Failed to stop automation'
      );
      // bulk_cancel rejects with `err_execution_not_cancellable` and friends —
      // map them so the toast says WHY, not just that it failed.
      toast({
        title: getAutomationApiErrorCopy(err, TOAST.cancelFailed),
        status: 'error',
      });
    }
  }, [activity, cancelExecutions, dismiss, setStatus, toast]);

  // Re-fire the trigger after a failure. The timer manager only fires
  // `pending` rows once, so a failed row needs this manual path.
  const handleRetry = useCallback(async () => {
    setStatus(activity.id, 'running');
    try {
      await fire(activity);
      setStatus(activity.id, 'completed');
    } catch (err) {
      setStatus(
        activity.id,
        'failed',
        err instanceof Error ? err.message : 'Failed to start automation'
      );
      toast({
        title: getAutomationApiErrorCopy(err, TOAST.triggerFailed),
        status: 'error',
      });
    }
  }, [activity, fire, setStatus, toast]);

  const candidateCount = activity.candidates.length;
  const KindIcon = getActivityKindIcon(activity.activityKind as ActivityKind);

  const isPaused = activity.status === 'paused';
  const isPending = activity.status === 'pending';
  const isRunning = activity.status === 'running';
  const isCompleted = activity.status === 'completed';
  const isFailed = activity.status === 'failed';
  const isCancelled = activity.status === 'cancelled';
  // Delayed first step → "scheduled" copy + a "Sends in X" line (the row still
  // runs the 15s undo → trigger; only the wording differs).
  const isDelayed = activity.delayed === true;

  // Every item is its own accordion: an always-visible header row + a
  // collapsible body, toggled independently. `isBulkStyle` keeps the
  // bulk (gray strip) vs single (white card) header treatment — only the
  // collapse behaviour is shared across both.
  const isBulkStyle = activity.isBulk;
  const showBody = isExpanded;

  // The activity's own name (stage suffix dropped), e.g.
  // "Email - Application Review" → "Email"; a custom label is left as-is.
  const actionLabel = getActivityActionLabel(
    activity.activityKind,
    activity.activityLabel,
    activity.stageName
  );
  const activityName = stripStageSuffix(
    activity.activityLabel,
    activity.stageName
  );
  // Kind-based heading (PM matrix) — "Sending email(s)" / "Email(s) queued";
  // the activity NAME lives in the description. Failed / cancelled aren't in
  // the matrix, so they keep the label-based copy ("<label> failed/stopped").
  const title =
    getUnderwayHeading(activity) ??
    (isDelayed ? scheduledTitleByStatus : titleByStatus)(
      activity.status,
      activity.status === 'completed' ? activityName : actionLabel
    );

  return (
    <>
      <Box
        bg="base-white"
        borderBottom="1px solid"
        borderColor="border-color"
        _first={{ borderTop: '1px solid', borderColor: 'border-color' }}
        _last={{ borderBottom: 'none' }}
        // Row hover only drives the Stop affordance; pausing is widget-level.
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        data-test-id={`underway-item-${activity.id}`}
      >
        {/* Header. Bulk → gray accordion strip; single → flat white row.
            The lightning renders bare (no tinted-square background) for both. */}
        <Flex
          alignItems="center"
          gap="8px"
          bg={isPaused ? 'secondary-100' : 'secondary-25'}
          py="12px"
          px="20px"
          // Whole header toggles; inner action buttons stopPropagation.
          cursor="pointer"
          onClick={() => setIsExpanded((v) => !v)}
          data-test-id="automation-underway-header"
        >
          <Icon
            as={isPaused ? PauseSquare : Lightning01}
            w="18px"
            h="18px"
            color={isPaused ? 'secondary-700' : 'pastel-3-dark'}
          />
          <Icon as={KindIcon} w="18px" h="18px" color="secondary-700" />
          {/* Tooltip surfaces the full title only when it's actually
              ellipsised (KText gates the label on real overflow). */}
          <KText
            size="sm-semi-bold"
            color="secondary-900"
            flex="1"
            isTruncated
            enableTooltip
            tooltipText={title}
          >
            {title}
          </KText>
          {/* Right slot — depends on status.
              - pending (default) → loader spinner
              - pending + hovering / paused → Stop button (hover-to-
                pause is implicit via the row hover)
              - running → loader spinner
              - completed → green check (no dismiss; the popup-header
                close handles batch clearing)
              - failed → Retry + × dismiss
              - cancelled → red stop icon (mirrors the completed
                terminal-state treatment; no × dismiss) */}
          {isPending &&
            !isHovering &&
            (isBulkStyle ? (
              <Spinner size="sm" color="primary-500" />
            ) : (
              // Single layout surfaces the hover-to-pause affordance as
              // text (the design); bulk keeps the compact spinner.
              <Flex alignItems="center" gap="4px" flexShrink={0}>
                <PauseSquare
                  size={16}
                  color="var(--kula-colors-secondary-500)"
                  className="kula-icon"
                />
                <KText size="sm-regular" color="secondary-500">
                  {STR.hoverToPause}
                </KText>
              </Flex>
            ))}
          {/* Stop follows ROW hover (or its open modal); pause is widget-wide. */}
          {(((isPaused || isPending) && isHovering) || isStopModalOpen) && (
            <Flex
              as="button"
              type="button"
              alignItems="center"
              gap="4px"
              flexShrink={0}
              onClick={(e) => {
                e.stopPropagation();
                handleStopClick();
              }}
              _hover={{ opacity: 0.8 }}
              data-test-id="automation-underway-stop"
            >
              <StopSquare
                size={16}
                color="var(--kula-colors-error-200)"
                className="kula-icon"
              />
              <KText size="sm-semi-bold" color="error-200">
                {STR.stop}
              </KText>
            </Flex>
          )}
          {/* Widget-hover-paused rows show nothing here — the header's pause
              icon + gray strip already read as paused. */}
          {isRunning && <Spinner size="sm" color="primary-500" />}
          {isCompleted && <CompletedCheckBadge label="Automation completed" />}
          {isFailed && (
            <Flex
              as="button"
              type="button"
              alignItems="center"
              flexShrink={0}
              onClick={(e) => {
                e.stopPropagation();
                handleRetry();
              }}
              _hover={{ opacity: 0.8 }}
            >
              <KText size="sm-semi-bold" color="ats-primary">
                {STR.retry}
              </KText>
            </Flex>
          )}
          {isCancelled && (
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              color="error-200"
              flexShrink={0}
              aria-label="Automation stopped"
            >
              <StopSquare size={18} className="kula-icon" />
            </Box>
          )}
          {isFailed && (
            <Button
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                dismiss(activity.id);
              }}
            >
              ×
            </Button>
          )}
          {/* No onClick — the chevron's click bubbles to the header handler
              (a second one here would toggle twice = no-op). */}
          <Icon
            as={isExpanded ? ChevronUp : ChevronDown}
            w="16px"
            h="16px"
            color="secondary-500"
            flexShrink={0}
            aria-label={isExpanded ? STR.collapse : STR.expand}
            data-test-id="automation-underway-toggle"
          />
        </Flex>

        {/* Countdown bar — OUTSIDE the collapsible body (CORE-5113): a
            collapsed row still has to show that its automation is running,
            otherwise there's no signal at all once the details are hidden.
            Gradient + dims mirror BulkOperationQueue's SliderFilledTrack so
            the two popups read as one family. Smooth via inline width
            transitions. */}
        {(isPending || isPaused) && (
          <Box
            px="20px"
            pt="8px"
            // Only pays for its own bottom inset when the body below is
            // collapsed — expanded, the body's own padding follows.
            pb={showBody ? '0' : '12px'}
            data-test-id="automation-underway-progress"
          >
            <Box
              h="6px"
              bg="secondary-100"
              borderRadius="6px"
              overflow="hidden"
            >
              <Box
                h="100%"
                w={`${progressPct}%`}
                bg="linear-gradient(90deg, #8A90FF 0%, #B15FDA 100%)"
                borderRadius="6px"
                transition={isPaused ? 'none' : 'width 0.1s linear'}
              />
            </Box>
          </Box>
        )}

        {/* Body — description. Collapsible for bulk accordions; always shown
            for the single flat layout. */}
        <Box
          overflow="hidden"
          transition="all 0.15s ease-out"
          maxH={showBody ? '320px' : '0'}
          opacity={showBody ? 1 : 0}
          px="20px"
          // Collapse the bottom padding too — otherwise it renders as a ~12px
          // gap below the header while the body's maxH is 0.
          pb={showBody ? '12px' : '0'}
        >
          {/* Per-kind description sentence with inline pills. The copy
              structure (verb, preposition, reviewer/using clause) varies by
              activity kind — see `UnderwayItemDescription` / `activityCopy`. */}
          <UnderwayItemDescription
            activity={activity}
            activityName={activityName}
            onOpenCandidates={handleOpenCandidates}
          />
          {isFailed && activity.errorMessage && (
            <KText
              mt="6px"
              size="xs-regular"
              color="error-200"
              data-test-id="automation-underway-error"
            >
              {formatAutomationError(activity.errorMessage)}
            </KText>
          )}
        </Box>
      </Box>
      <StopAutomationConfirmModal
        isOpen={isStopModalOpen}
        candidateName={
          // Only named for the single-candidate case; bulk falls back
          // to the count + stage phrasing inside the modal.
          !activity.isBulk && candidateCount === 1
            ? (activity.candidates[0]?.name ?? undefined)
            : undefined
        }
        candidateCount={candidateCount}
        stageName={activity.stageName}
        onCancel={handleStopCancel}
        onConfirm={handleStopConfirm}
      />
      {activity.isBulk && (
        <SelectedCandidatesModal
          isOpen={isCandidatesModalOpen}
          onClose={() => {
            // Refresh the clock before flipping the modal off so the
            // first `pending` paint after resume uses a fresh nowMs
            // (same pattern as `handleStopCancel`).
            markResumeClock();
            setIsCandidatesModalOpen(false);
          }}
          activity={activity}
        />
      )}
    </>
  );
};
